import { useContext, useEffect, useMemo, useState } from 'react';

import { Delete, Edit } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    ThemeProvider,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { MaterialReactTable } from 'material-react-table';

import CrudsAPI from '../../../api/CRUDs';
import * as SubGrupoAPI from '../../../api/subGrupo';
import { Context } from '../../../context/UserContext';
import { getMRTDefaultProps } from '../../../helpers/mrt/getMRTDefaultProps';
import { getEmpresaScope } from '../../../helpers/sessionEmpresa';
import { useMRTListController } from '../../../hooks/useMRTController';
import { useMRTState } from '../../../hooks/useMRTState';
import { podeCancelar, podeEditar, podeIncluir } from '../../../hooks/usePermisionMRT';
import useFlashMessage from '../../../hooks/useFlashMessage';
import { useNavigateParams } from '../../../hooks/useNavigateParams';
import { obterPermissoesUsuario } from '../../../utils/funcoes';
import tableThemeCustom from '../../form/tableThemeCustom';
import CustomDialog from '../../layout/CustomDialog';

const BOOLEAN_FILTER_OPTIONS = [
    { label: 'Todos', value: '' },
    { label: 'Sim', value: '1' },
    { label: 'Não', value: '0' },
];

function valorBooleano(value) {
    return value === true || Number(value) === 1;
}

function simNao(value) {
    return valorBooleano(value) ? 'Sim' : 'Não';
}

function formatarDataHora(value) {
    if (!value) return '';

    const data = new Date(value);

    return Number.isNaN(data.getTime()) ? '' : data.toLocaleString('pt-BR');
}

function mensagemErro(error, fallback) {
    return error?.response?.data?.message || fallback;
}

function SubGruposDetailPanel({ grupo }) {
    const [subGrupos, setSubGrupos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let ativo = true;

        const carregar = async () => {
            setLoading(true);
            setError('');

            try {
                const response = await SubGrupoAPI.listar(grupo.id);

                if (ativo) {
                    setSubGrupos(Array.isArray(response.data?.data) ? response.data.data : []);
                }
            } catch (requestError) {
                if (ativo) {
                    setError(mensagemErro(requestError, 'Não foi possível carregar os subgrupos.'));
                }
            } finally {
                if (ativo) setLoading(false);
            }
        };

        if (grupo?.id) {
            carregar();
        } else {
            setLoading(false);
            setError('Grupo não identificado.');
        }

        return () => {
            ativo = false;
        };
    }, [grupo?.id]);

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                Subgrupos de {grupo.descricao}
            </Typography>

            {loading && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
                    <CircularProgress size={20} />
                    <Typography color="text.secondary">Carregando subgrupos...</Typography>
                </Stack>
            )}

            {!loading && error && <Alert severity="error">{error}</Alert>}

            {!loading && !error && !subGrupos.length && (
                <Typography color="text.secondary">Nenhum subgrupo cadastrado.</Typography>
            )}

            {!loading && !error && subGrupos.length > 0 && (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small" aria-label={`Subgrupos de ${grupo.descricao}`}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Descrição</TableCell>
                                <TableCell align="center" width={100}>
                                    Ativo
                                </TableCell>
                                <TableCell width={180}>Atualizado em</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {subGrupos.map((subGrupo) => (
                                <TableRow key={subGrupo.uuid || subGrupo.id} hover>
                                    <TableCell>{subGrupo.descricao}</TableCell>
                                    <TableCell align="center">{simNao(subGrupo.ativo)}</TableCell>
                                    <TableCell>{formatarDataHora(subGrupo.updatedAt)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}

function Index() {
    const { parametro } = useContext(Context);
    const { pessoaId, empresaId } = getEmpresaScope();
    const { setFlashMessage } = useFlashMessage();
    const navigate = useNavigateParams();

    const [token] = useState(localStorage.getItem('token') || '');
    const [dados, setDados] = useState([]);
    const [grupoExcluir, setGrupoExcluir] = useState(null);

    const urlParams = new URLSearchParams(window.location.search);
    const pathname = '/Grupos/CreateOrEdit';
    const ul = urlParams.get('ul') || 2;
    const li = urlParams.get('li') || 40;
    const tipo = urlParams.get('tipo');
    const operacaoUUID = urlParams.get('operacao');

    const theme = useMemo(() => tableThemeCustom(), []);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const mrt = useMRTState({
        storageKey: 'grupos.index',
        initialState: {
            columnFilterFns: {},
            columnFilters: [],
            globalFilter: '',
            sorting: [],
            pagination: {
                pageIndex: 0,
                pageSize: parametro?.paginacao || 10,
            },
        },
    });

    const mrtDefault = getMRTDefaultProps({
        mrt,
        parametro,
        isMobile,
    });

    const criarParams = () =>
        new URLSearchParams({
            ul,
            li,
            ...(tipo ? { tipo } : {}),
            ...(operacaoUUID ? { operacao: operacaoUUID } : {}),
        });

    const fetchData = async () => {
        dados.length ? mrt.setIsRefetching(true) : mrt.setIsLoading(true);
        mrt.setIsError(false);

        try {
            const response = await CrudsAPI.grupo.listar(
                mrt.pagination.pageIndex * mrt.pagination.pageSize,
                mrt.pagination.pageSize,
                JSON.stringify(mrt.columnFilters ?? []),
                JSON.stringify(mrt.columnFilterFns ?? {}),
                mrt.globalFilter ?? '',
                JSON.stringify(mrt.sorting ?? [])
            );

            setDados(response.data.data || []);
            mrt.setRowCount(Number(response.data.meta?.totalRowCount) || 0);
        } catch (error) {
            console.error(error);
            mrt.setIsError(true);
        } finally {
            mrt.setIsLoading(false);
            mrt.setIsRefetching(false);
        }
    };

    useMRTListController({
        mrt,
        contextKey: 'grupos.index',
        contextReady: Boolean(token),
        bootMode: 'simple',
        fetchData,
        resolveBoot: () => ({
            sorting: [{ id: 'descricao', desc: false }],
        }),
    });

    useEffect(() => {
        if (!token) {
            navigate('/Auth', { replace: true });
            return;
        }

        obterPermissoesUsuario(pessoaId, empresaId, ul, li);
    }, [empresaId, li, navigate, pessoaId, token, ul]);

    const handleNovo = () => {
        const params = criarParams();
        params.set('btnText', 'Cadastrar');
        navigate(pathname, params);
    };

    const handleEditar = (row) => {
        const params = criarParams();
        params.set('uuid', row.id);
        params.set('btnText', 'Atualizar');
        navigate(pathname, params);
    };

    const handleConfirmarExclusao = async () => {
        if (!grupoExcluir) return;

        try {
            const response = await CrudsAPI.grupo.deletar(grupoExcluir);
            setFlashMessage(response.data.message, response.data.type || 'success');
            setGrupoExcluir(null);
            await fetchData();
        } catch (error) {
            console.error(error);
            setFlashMessage(mensagemErro(error, 'Não foi possível excluir o grupo.'), error?.response?.data?.type || 'error');
        }
    };

    const columns = useMemo(
        () => [
            {
                id: 'grupo',
                header: 'Grupos',
                columns: [
                    {
                        accessorKey: 'Empresa.nomeFantasia',
                        header: 'Empresa',
                    },
                    {
                        accessorKey: 'descricao',
                        header: 'Descrição',
                    },
                    {
                        accessorKey: 'produto',
                        header: 'Produto',
                        columnFilterModeOptions: ['equals'],
                        filterSelectOptions: BOOLEAN_FILTER_OPTIONS,
                        filterVariant: 'select',
                        Cell: ({ cell }) => <Box sx={{ textAlign: 'center' }}>{simNao(cell.getValue())}</Box>,
                    },
                    {
                        accessorKey: 'centroCusto',
                        header: 'Centro Custo',
                        columnFilterModeOptions: ['equals'],
                        filterSelectOptions: BOOLEAN_FILTER_OPTIONS,
                        filterVariant: 'select',
                        Cell: ({ cell }) => <Box sx={{ textAlign: 'center' }}>{simNao(cell.getValue())}</Box>,
                    },
                    {
                        accessorKey: 'ativo',
                        header: 'Ativo',
                        columnFilterModeOptions: ['equals'],
                        filterSelectOptions: BOOLEAN_FILTER_OPTIONS,
                        filterVariant: 'select',
                        Cell: ({ cell }) => <Box sx={{ textAlign: 'center' }}>{simNao(cell.getValue())}</Box>,
                    },
                ],
            },
        ],
        []
    );

    return (
        <div>
            <CustomDialog
                open={Boolean(grupoExcluir)}
                title="Excluir Grupo"
                content={`Confirma a exclusão do grupo ${grupoExcluir?.original?.descricao || ''}?`}
                onClose={() => setGrupoExcluir(null)}
                onConfirm={handleConfirmarExclusao}
                confirmLabel="Excluir"
                confirmColor="error"
            />

            <ThemeProvider theme={theme}>
                <MaterialReactTable
                    {...mrtDefault}
                    columns={columns}
                    data={dados}
                    getRowId={(row) => row.uuid}
                    enableRowSelection={false}
                    renderDetailPanel={({ row }) => <SubGruposDetailPanel grupo={row.original} />}
                    renderRowActions={({ row }) => (
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                            <Tooltip arrow placement="left" title="Editar">
                                <span>
                                    <IconButton type="button" onClick={() => handleEditar(row)} disabled={!podeEditar()}>
                                        <Edit />
                                    </IconButton>
                                </span>
                            </Tooltip>

                            <Tooltip arrow placement="right" title="Excluir">
                                <span>
                                    <IconButton
                                        type="button"
                                        color="error"
                                        onClick={() => setGrupoExcluir(row)}
                                        disabled={!podeCancelar()}
                                    >
                                        <Delete />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    )}
                    renderTopToolbarCustomActions={() => (
                        <Tooltip arrow placement="top-end" title="Novo registro">
                            <span>
                                <Button
                                    type="button"
                                    color="secondary"
                                    onClick={handleNovo}
                                    variant="contained"
                                    disabled={!podeIncluir()}
                                >
                                    Novo
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                />
            </ThemeProvider>
        </div>
    );
}

export default Index;
